import React from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { SettingsResponse } from '@/api/types';
import { SeityPanel } from '@/components/settings/panels/AlterEgoPanel';
import { ConnectorsPanel } from '@/components/settings/panels/ConnectorsPanel';
import { VoicePanel } from '@/components/settings/panels/VoicePanel';
import { GovernorPanel } from '@/components/settings/panels/GovernorPanel';
import { CanonPanel } from '@/components/settings/panels/CanonPanel';
import { DataMgmtPanel } from '@/components/settings/panels/DataMgmtPanel';
import { NotificationSettingsPanel } from '@/components/settings/panels/NotificationSettingsPanel';
import { MemoryLegibilityPanel } from '@/components/settings/panels/MemoryLegibilityPanel';
import { TrustBondPanel } from '@/components/settings/panels/TrustBondPanel';
import { TalentsPanel } from '@/components/settings/panels/TalentsPanel';
import { AgentsPanel } from '@/components/settings/panels/AgentsPanel';
import { RuntimePanel } from '@/components/settings/panels/RuntimePanel';
import { WritingDeskPanel } from '@/components/settings/panels/WritingDeskPanel';
import { PanelErrorBoundary } from '@/components/settings/panels/routing/PanelErrorBoundary';
import { ModelsPanel } from '@/components/settings/panels/routing/ModelsPanel';
import { KeysAndAccountsPanel } from '@/components/settings/panels/routing/KeysAndAccountsPanel';
import { RoutingPanel } from '@/components/settings/panels/routing/RoutingPanel';
import { SpendingAndUsagePanel } from '@/components/settings/panels/routing/SpendingAndUsagePanel';
import { LocalAndAdvancedPanel } from '@/components/settings/panels/routing/LocalAndAdvancedPanel';
import {
  LinkedSurfacePanel,
  PanelWrapper,
  titleCase,
  type SettingsRouteSectionId,
} from '@/components/settings/settingsSpineConfig';

export function SettingsSpineContent(props: {
  activeSection: SettingsRouteSectionId;
  snapshot: SettingsResponse | null;
  navigate: NavigateFunction;
  /** Route page uses framed copy; workbench uses the same panels without extra chrome. */
  variant?: 'route' | 'workbench';
  /** Optional: show connected source count next to connectors (workbench). */
  connectedSourceCount?: number;
}) {
  const { activeSection, snapshot, navigate, variant = 'route', connectedSourceCount } = props;
  const isRoute = variant === 'route';

  return (
    <>
      {activeSection === 'seity' ? <SeityPanel mode="tenant" /> : null}
      {activeSection === 'trust' ? <TrustBondPanel /> : null}
      {activeSection === 'desk' ? (
        isRoute ? (
          <PanelWrapper
            title="Writing desk"
            description="Every field here is a control the writer sets. Nothing about how text arrived is inferred, and no score of any kind is kept."
          >
            <WritingDeskPanel />
          </PanelWrapper>
        ) : (
          <WritingDeskPanel />
        )
      ) : null}
      {activeSection === 'voice' ? <VoicePanel mode="tenant" /> : null}
      {activeSection === 'standards' ? <GovernorPanel mode="tenant" /> : null}
      {activeSection === 'taste' ? <CanonPanel /> : null}
      {activeSection === 'models' ? (
        <PanelErrorBoundary panelLabel="Models">
          <ModelsPanel />
        </PanelErrorBoundary>
      ) : null}
      {activeSection === 'keys' ? (
        <PanelErrorBoundary panelLabel="Keys and accounts">
          <KeysAndAccountsPanel />
        </PanelErrorBoundary>
      ) : null}
      {activeSection === 'routing' ? (
        <PanelErrorBoundary panelLabel="Routing">
          <RoutingPanel />
        </PanelErrorBoundary>
      ) : null}
      {activeSection === 'spending' ? (
        <PanelErrorBoundary panelLabel="Spending and usage">
          <SpendingAndUsagePanel />
        </PanelErrorBoundary>
      ) : null}
      {activeSection === 'local' ? (
        <PanelErrorBoundary panelLabel="Local and advanced">
          <LocalAndAdvancedPanel />
        </PanelErrorBoundary>
      ) : null}
      {activeSection === 'talents' ? (
        isRoute ? (
          <PanelWrapper
            title="Talents and famili"
            description="This canonical route uses the same settings-native talents lane as the modal and workbench shells. Per-famili controls stay summary-first until the backend exposes them."
          >
            <TalentsPanel mode="tenant" />
          </PanelWrapper>
        ) : (
          <TalentsPanel mode="tenant" />
        )
      ) : null}
      {activeSection === 'connectors' ? (
        <div className="space-y-4">
          <ConnectorsPanel mode="tenant" />
          {typeof connectedSourceCount === 'number' ? (
            <p className="text-xs text-gray-400">
              Connected sources: <span className="text-gray-200">{connectedSourceCount}</span>
            </p>
          ) : null}
        </div>
      ) : null}
      {activeSection === 'memory' ? <MemoryLegibilityPanel /> : null}
      {activeSection === 'notifications' ? <NotificationSettingsPanel /> : null}
      {activeSection === 'data' ? <DataMgmtPanel mode="tenant" /> : null}
      {activeSection === 'billing' ? (
        <LinkedSurfacePanel
          title="Billing page"
          description="Subscription, checkout, usage, and credits remain on the dedicated billing route. Open it for the full billing app."
          ctaLabel="Open billing"
          onOpen={() => navigate('/settings/billing')}
          footer={
            snapshot ? (
              <div className="grid gap-3 md:grid-cols-3">
                <SummaryCard label="Tier" value={titleCase(snapshot.subscription_tier)} />
                <SummaryCard label="Status" value={titleCase(snapshot.subscription_status)} />
                <SummaryCard label="Provider" value={snapshot.billing_provider || 'Not set'} />
              </div>
            ) : null
          }
        />
      ) : null}
      {activeSection === 'agents' ? <AgentsPanel /> : null}
      {activeSection === 'runtime' ? <RuntimePanel /> : null}
    </>
  );
}

const SummaryCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl border px-4 py-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}>
    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>
      {label}
    </div>
    <div className="mt-2 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
      {value}
    </div>
  </div>
);
